import { memo, useEffect, useRef } from 'react'
import { checkInputErrorFileMaxSize, useFile } from '@chilecompra/react-kit'
import { Button, Typography } from '@chilecompra/react-kit/components'
import PropTypes from 'prop-types'

import { ACCEPTED_DOCUMENT_TYPES, MAX_MB } from '../../../config/settings/constants'
import { formatFileSize } from '../../../utils/formatFileSize'
import {
  AttachedFiles,
  FileCard,
  FileIcon,
  FileInfo,
  FileUploadInfo,
  FileUploadWrapper,
  HiddenInput,
  RemoveIcon
} from '../Claims.styles'

/**
 * The Claims attached files.
 */
const ClaimsAttachedFiles = props => {
  const { attachedFiles = [], isDisabled = false, onAdd, onRemove } = props

  const inputRef = useRef(null)

  const {
    error: fileError,
    onChange: handleFileChange,
    onReset: handleFileReset,
    setValue: setFileValue,
    value: fileValue
  } = useFile({
    errorCallbacks: [checkInputErrorFileMaxSize(MAX_MB)]
  })

  useEffect(() => {
    if (fileError !== '') return setFileValue('')

    if (fileValue) onAdd(fileValue)
  }, [fileError, fileValue, onAdd, setFileValue])

  useEffect(() => {
    if (attachedFiles.length === 0) handleFileReset()
  }, [attachedFiles, handleFileReset])

  const handleButtonClick = () => {
    if (inputRef.current) inputRef.current.click()
  }

  const handleInputChange = event => {
    const files = event.target.files

    if (files && files.length > 0) {
      handleFileChange(files)
    }

    event.target.value = ''
  }

  return (
    <AttachedFiles direction="column" spacing={2}>
      <Typography fontWeight="bold" variant="h4">
        Adjuntar documentos
      </Typography>

      <FileUploadWrapper>
        <FileUploadInfo direction="column" spacing={0.5}>
          <Typography fontWeight="bold" variant="body2">
            Documentación
          </Typography>

          <Typography color="black3" variant="body2">
            Formatos permitidos: csv, doc, docx, jpg, pdf, xls, xlsx
          </Typography>

          <Typography color="black3" variant="body2">
            Peso máximo: {MAX_MB} MB
          </Typography>
        </FileUploadInfo>

        <HiddenInput
          accept={ACCEPTED_DOCUMENT_TYPES}
          onChange={handleInputChange}
          ref={inputRef}
          type="file"
        />

        <Button
          color="primary"
          disabled={isDisabled}
          onClick={handleButtonClick}
          variant="outlined"
        >
          {fileValue ? 'Agregar otro archivo' : 'Seleccionar archivo'}
        </Button>
      </FileUploadWrapper>

      {fileError && (
        <Typography color="error" variant="body2">
          {fileError}
        </Typography>
      )}

      {attachedFiles.length > 0 && (
        <>
          <Typography fontWeight="bold" variant="body2">
            Archivos adjuntos ({attachedFiles.length})
          </Typography>

          {attachedFiles.map(file => (
            <FileCard key={file.id}>
              <FileIcon />

              <FileInfo>
                <Typography className="file__name" variant="body2">
                  {file.fileName}
                </Typography>

                <Typography className="file__size" variant="caption">
                  {formatFileSize(file.file?.size)}
                </Typography>
              </FileInfo>

              <RemoveIcon disabled={isDisabled} onClick={onRemove(file.id)} />
            </FileCard>
          ))}
        </>
      )}
    </AttachedFiles>
  )
}

ClaimsAttachedFiles.propTypes = {
  attachedFiles: PropTypes.array,
  isDisabled: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

export default memo(ClaimsAttachedFiles)
